import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { ArrowRightLeft, RefreshCw } from 'lucide-react';
import { apiErrorMessage, getTapeSurveillance } from '../api/abfApi';
import type { ActionFeedbackState } from '../components/ActionFeedback';
import { fmtDate, fmtMoney, fmtNumber, fmtPct, labelFor, plural } from '../presentation/format';
import {
  normalizeRollRateCell, orderRollStates, type NormalizedRollRateCell, type TapeSurveillance,
} from '../types/tapeSurveillance';

/**
 * F-MK-075 — how the pool's loans moved between the two latest released tapes: from each state on
 * the prior tape to each state on the current one, by count and by the share of the prior
 * bucket's balance. Status-first bucketing (paid off, charged off, defaulted before delinquency)
 * is the server's; this card only lays it out.
 */

interface Props {
  facilityId: number;
  currency?: string | null;
  onFeedback: (feedback: ActionFeedbackState) => void;
}

const Card = styled.section`
  padding: 1rem; border: 1px solid rgba(148,163,184,.16); border-radius: 12px; background: rgba(8,16,30,.8);
  h3 { margin: 0; color: #f8fafc; font-size: 15px; }
  p.sub { margin: .3rem 0 0; color: #94a3b8; font-size: 12px; }
  p.empty { margin: .9rem 0 0; color: #94a3b8; font-size: 13px; }
  button { display: inline-flex; align-items: center; gap: 6px; min-height: 34px; padding: .35rem .7rem; border: 1px solid rgba(148,163,184,.24); border-radius: 8px; color: #cbd5e1; background: rgba(15,23,42,.6); cursor: pointer; font: inherit; font-size: 13px; }
  .scroll { overflow-x: auto; margin-top: .9rem; }
  table { width: 100%; border-collapse: collapse; font-size: 12px; font-variant-numeric: tabular-nums; }
  th { text-align: left; padding: .45rem .5rem; color: #7dd3fc; font-size: 11px; font-weight: 650; text-transform: uppercase; letter-spacing: .05em; border-bottom: 1px solid rgba(148,163,184,.2); white-space: nowrap; }
  td { padding: .45rem .5rem; border-top: 1px solid rgba(148,163,184,.1); color: #e2e8f0; vertical-align: top; }
  th.num, td.num { text-align: right; white-space: nowrap; }
  td.from strong { display: block; color: #f8fafc; }
  td.from small { color: #94a3b8; }
  td[data-stay='true'] { color: #94a3b8; }
  td[data-worse='true'] { color: #fcd34d; }
  td[data-exit='true'] { color: #cbd5e1; }
  td small { display: block; color: #94a3b8; font-size: 11px; }
  .boundary { margin-top: .7rem; color: #94a3b8; font-size: 11px; line-height: 1.5; }
`;

const SEVERITY: Record<string, number> = { CURRENT: 0, LATE_30: 1, LATE_60: 2, LATE_90_PLUS: 3, DEFAULTED: 4, CHARGED_OFF: 5 };
const EXITS = new Set(['PAID_OFF', 'CHARGED_OFF']);

export interface RollRateRow {
  fromState: string;
  loans: number;
  priorBalance: number | null;
  cells: Record<string, NormalizedRollRateCell>;
}

/** Rows by prior state in canonical order, each with its loan count and prior balance; rates by count when the payload has no balance share. */
export const rollRateRows = (cells: NormalizedRollRateCell[]): { rows: RollRateRow[]; toStates: string[]; rateBasis: 'BALANCE' | 'COUNT' } => {
  const byFrom = new Map<string, RollRateRow>();
  for (const cell of cells) {
    const row = byFrom.get(cell.fromState) || { fromState: cell.fromState, loans: 0, priorBalance: null, cells: {} };
    row.loans += cell.assetCount;
    if (cell.priorBalance !== null) row.priorBalance = (row.priorBalance ?? 0) + cell.priorBalance;
    row.cells[cell.toState] = cell;
    byFrom.set(cell.fromState, row);
  }
  const rateBasis: 'BALANCE' | 'COUNT' = cells.some(cell => cell.ratePct !== null) ? 'BALANCE' : 'COUNT';
  const rows = orderRollStates(byFrom.keys()).map(state => byFrom.get(state)!);
  if (rateBasis === 'COUNT') {
    for (const row of rows) {
      for (const cell of Object.values(row.cells)) cell.ratePct = row.loans > 0 ? (cell.assetCount / row.loans) * 100 : null;
    }
  }
  return { rows, toStates: orderRollStates(cells.map(cell => cell.toState)), rateBasis };
};

export const rollRateEmptySentence = (tapeCount: number | null | undefined): string =>
  tapeCount === 0 || tapeCount === null || tapeCount === undefined
    ? 'Roll rates need two released tapes; none has been released yet.'
    : tapeCount === 1
      ? 'Roll rates need two released tapes; only one has been released.'
      : 'Roll rates need two released tapes; the latest release has no earlier release to compare with.';

const RollRateCard: React.FC<Props> = ({ facilityId, currency, onFeedback }) => {
  const [data, setData] = useState<TapeSurveillance | null>(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await getTapeSurveillance(facilityId));
    } catch (error) {
      onFeedback({ tone: 'error', message: apiErrorMessage(error, 'The roll rates could not be loaded.') });
    } finally {
      setLoading(false);
    }
  }, [facilityId, onFeedback]);
  useEffect(() => { void load(); }, [load]);

  const cells = useMemo(() => (data?.rollRates || []).map(normalizeRollRateCell), [data]);
  const { rows, toStates, rateBasis } = useMemo(() => rollRateRows(cells), [cells]);
  const latestAsOf = data?.latestTape && typeof data.latestTape.as_of_date === 'string' ? data.latestTape.as_of_date : null;
  const ccy = currency || 'USD';

  return (
    <Card aria-label="Roll rates" aria-busy={loading}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div>
          <h3><ArrowRightLeft size={15} style={{ verticalAlign: 'middle', marginRight: 6 }} />Roll rates</h3>
          <p className="sub">{rows.length
            ? `Loans moving between states from the prior released tape to the one of ${latestAsOf ? fmtDate(latestAsOf) : 'the latest release'} · each cell is the loan count and the share of the prior state's ${rateBasis === 'BALANCE' ? 'balance' : 'loans'} that moved.`
            : loading ? 'Loading…' : 'Where the pool\'s loans went between the two latest released tapes.'}</p>
        </div>
        <button type="button" onClick={() => void load()} disabled={loading}><RefreshCw size={14} />Refresh</button>
      </div>

      {!loading && rows.length === 0 && <p className="empty">{rollRateEmptySentence(data?.tapeCount)}</p>}

      {rows.length > 0 && <div className="scroll">
        <table>
          <thead><tr><th>From (prior tape)</th>{toStates.map(state => <th key={state} className="num">{labelFor(state)}</th>)}</tr></thead>
          <tbody>{rows.map(row => <tr key={row.fromState}>
            <td className="from"><strong>{labelFor(row.fromState)}</strong><small>{plural(row.loans, 'loan')}{row.priorBalance !== null ? ` · ${fmtMoney(row.priorBalance, ccy)}` : ''}</small></td>
            {toStates.map(state => {
              const cell = row.cells[state];
              if (!cell) return <td key={state} className="num">—</td>;
              const stay = state === row.fromState;
              const worse = !stay && (SEVERITY[state] ?? -1) > (SEVERITY[row.fromState] ?? -1);
              return <td key={state} className="num" data-stay={stay || undefined} data-worse={worse || undefined} data-exit={EXITS.has(state) || undefined}
                title={cell.currentBalance !== null ? `${fmtMoney(cell.currentBalance, ccy)} current balance` : undefined}>
                {fmtNumber(cell.assetCount, 0)}<small>{cell.ratePct === null ? 'no rate' : fmtPct(cell.ratePct, 1)}</small>
              </td>;
            })}
          </tr>)}</tbody>
        </table>
      </div>}
      {rows.length > 0 && <div className="boundary">A loan's state is its asset status when that ends or impairs it (paid off, charged off, defaulted), otherwise its delinquency bucket. "New to the pool" loans were not on the prior tape; loans absent from the current tape read as paid off unless they had already left the pool.{rateBasis === 'COUNT' ? ' This surveillance record predates balance shares, so rates here are by loan count.' : ''}</div>}
    </Card>
  );
};

export default RollRateCard;
